import type { Disposable } from "@/shared/contracts/lifecycle";
import { HoverChargeExecutionController } from "@/features/hoverIdle/core/hoverChargeExecutionController";
import {
  getCurrentLocale,
  getMessages,
  setCurrentLocale,
  type SupportedLocale,
} from "@/i18n/runtime";
import type { HoverPointerModel } from "@/features/hoverIdle/input/hoverPointerModel";
import type { HoverPointerSnapshot } from "@/features/hoverIdle/input/hoverPointerTypes";
import { resolveHoverActive } from "@/features/hoverIdle/input/resolveHoverActive";

interface FirstAccessTutorialOverlayOptions {
  readonly container: HTMLElement;
  readonly pointerModel: HoverPointerModel;
  readonly isTouchPrimary: boolean;
  readonly onComplete: () => void;
  readonly onLanguageChange: (locale: SupportedLocale) => void;
}

const DEFAULT_HOLD_DURATION_MS = 5_000;

const LOCALE_FLAG: Record<SupportedLocale, string> = {
  ja: "🇯🇵",
  en: "🇺🇸",
};

const LOCALE_LABEL: Record<SupportedLocale, string> = {
  ja: "日本語",
  en: "English",
};

export class FirstAccessTutorialOverlay implements Disposable {
  private readonly root: HTMLDivElement;
  private readonly okButton: HTMLButtonElement;
  private readonly fill: HTMLDivElement;
  private readonly chargeController: HoverChargeExecutionController;
  private readonly holdDurationMs: number;
  private readonly onComplete: () => void;
  private readonly onLanguageChange: (locale: SupportedLocale) => void;
  private readonly isTouchPrimary: boolean;
  private readonly wellcomeEl: HTMLHeadingElement;
  private readonly messageEl: HTMLDivElement;
  private readonly okLabelEl: HTMLSpanElement;
  private readonly langButtons: Map<SupportedLocale, HTMLButtonElement> =
    new Map();
  private readonly langButtonCleanups: Array<() => void> = [];
  private readonly pointerModel: HoverPointerModel;
  private readonly unsubscribePointer: () => void;
  private completed = false;
  private virtualPointerInsideOk = false;

  constructor(options: FirstAccessTutorialOverlayOptions) {
    this.holdDurationMs = DEFAULT_HOLD_DURATION_MS;
    this.onComplete = options.onComplete;
    this.onLanguageChange = options.onLanguageChange;
    this.pointerModel = options.pointerModel;

    const messages = getMessages();
    this.isTouchPrimary = options.isTouchPrimary;

    this.root = document.createElement("div");
    this.root.className = "hover-idle-tutorial-overlay";
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "true");
    this.root.hidden = true;

    const card = document.createElement("div");
    card.className = "hover-idle-tutorial-card";

    this.wellcomeEl = document.createElement("h2");
    this.wellcomeEl.className = "hover-idle-tutorial-wellcome";
    this.wellcomeEl.innerHTML = messages.tutorial.wellcome;

    this.messageEl = document.createElement("div");
    this.messageEl.className = "hover-idle-tutorial-message";
    this.renderMessageSteps();

    this.okButton = document.createElement("button");
    this.okButton.type = "button";
    this.okButton.className =
      "hover-idle-charge-up-icon hover-idle-tutorial-ok";
    this.okButton.setAttribute("aria-label", messages.tutorial.ok);
    this.okButton.dataset.engaged = "false";
    this.okButton.dataset.virtualPointer = "allow";

    const clip = document.createElement("div");
    clip.className = "hover-idle-charge-up-icon-clip";

    this.fill = document.createElement("div");
    this.fill.className = "hover-idle-charge-up-icon-fill";

    this.okLabelEl = document.createElement("span");
    this.okLabelEl.className = "hover-idle-charge-up-icon-label";
    this.okLabelEl.textContent = messages.tutorial.ok;

    const langSelect = document.createElement("div");
    langSelect.className = "hover-idle-segmented";
    langSelect.setAttribute("role", "group");
    langSelect.dataset.virtualPointer = "allow";

    const localeOrder: readonly SupportedLocale[] = ["ja", "en"];
    for (const locale of localeOrder) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "hover-idle-segmented-option";
      btn.dataset.locale = locale;
      btn.dataset.active = locale === getCurrentLocale() ? "true" : "false";
      btn.textContent = `${LOCALE_FLAG[locale]} ${LOCALE_LABEL[locale]}`;

      const handler = (): void => {
        this.handleLangClick(locale);
      };
      btn.addEventListener("pointerenter", handler);
      btn.addEventListener("focus", handler);
      btn.addEventListener("click", handler);
      this.langButtonCleanups.push(() => {
        btn.removeEventListener("pointerenter", handler);
        btn.removeEventListener("focus", handler);
        btn.removeEventListener("click", handler);
      });
      this.langButtons.set(locale, btn);
      langSelect.append(btn);
    }

    clip.append(this.fill, this.okLabelEl);
    this.okButton.append(clip);
    card.append(this.wellcomeEl, this.messageEl, this.okButton, langSelect);
    this.root.append(card);
    options.container.append(this.root);

    this.chargeController = new HoverChargeExecutionController({
      getDurationMs: () => this.holdDurationMs,
      canExecute: () => !this.completed && !this.root.hidden,
      onProgress: (progress) => this.applyHoldProgress(progress),
      onExecute: () => this.complete(),
    });

    this.okButton.addEventListener("pointerenter", this.handlePointerEnter);
    this.okButton.addEventListener("pointerleave", this.handlePointerLeave);
    this.okButton.addEventListener("focus", this.handleFocus);
    this.okButton.addEventListener("blur", this.handleBlur);
    this.okButton.addEventListener("click", this.preventClick);

    this.unsubscribePointer = this.pointerModel.subscribe((snapshot) => {
      this.handlePointerSnapshot(snapshot);
    });
  }

  isVisible(): boolean {
    return !this.root.hidden;
  }

  show(): void {
    this.root.hidden = false;
    // Drop any stale virtual-pointer position so the OK rect intersection
    // starts from a clean state when the dialog opens.
    this.pointerModel.clearVirtualTouchPosition();
    this.virtualPointerInsideOk = false;
    this.setBackgroundInert(true);
  }

  hide(): void {
    this.cancelHold();
    this.virtualPointerInsideOk = false;
    this.setBackgroundInert(false);
    this.root.hidden = true;
    // Re-evaluate hover intents now that background is interactive again so
    // a pointer left over the HovTarget begins gaining immediately.
    this.pointerModel.republish();
  }

  dispose(): void {
    this.cancelHold();
    this.chargeController.dispose();
    this.setBackgroundInert(false);
    this.unsubscribePointer();
    this.okButton.removeEventListener("pointerenter", this.handlePointerEnter);
    this.okButton.removeEventListener("pointerleave", this.handlePointerLeave);
    this.okButton.removeEventListener("focus", this.handleFocus);
    this.okButton.removeEventListener("blur", this.handleBlur);
    this.okButton.removeEventListener("click", this.preventClick);
    for (const cleanup of this.langButtonCleanups) cleanup();
    this.root.remove();
  }

  private handleLangClick(locale: SupportedLocale): void {
    if (locale === getCurrentLocale()) return;
    setCurrentLocale(locale);
    this.onLanguageChange(locale);
    this.refreshText();
    this.syncLangButtons();
  }

  private refreshText(): void {
    const messages = getMessages();
    this.wellcomeEl.innerHTML = messages.tutorial.wellcome;
    this.renderMessageSteps();
    this.okLabelEl.textContent = messages.tutorial.ok;
    this.okButton.setAttribute("aria-label", messages.tutorial.ok);
  }

  // Render each tutorial step as its own <p> so paragraph spacing is owned by
  // CSS instead of "\n" / innerHTML, and text stays injection-safe. Iterating
  // the step bundle's own values lets PC and mobile diverge in step count and
  // shape without coupling render order to a hard-coded key list.
  private renderMessageSteps(): void {
    const { tutorial } = getMessages();
    const steps: readonly string[] = this.isTouchPrimary
      ? Object.values(tutorial.mobileSteps)
      : Object.values(tutorial.pcSteps);
    this.messageEl.replaceChildren();
    for (const text of steps) {
      if (text.length === 0) continue;
      const paragraph = document.createElement("p");
      paragraph.className = "hover-idle-tutorial-message-step";
      paragraph.textContent = text;
      this.messageEl.append(paragraph);
    }
  }

  private syncLangButtons(): void {
    const current = getCurrentLocale();
    for (const [locale, btn] of this.langButtons) {
      btn.dataset.active = locale === current ? "true" : "false";
    }
  }

  private readonly preventClick = (event: MouseEvent): void => {
    event.preventDefault();
  };

  private readonly handlePointerEnter = (event: PointerEvent): void => {
    if (event.pointerType === "touch") {
      return;
    }
    this.beginHold();
  };

  private readonly handlePointerLeave = (): void => {
    if (this.virtualPointerInsideOk) {
      return;
    }
    this.releaseHold();
  };

  private readonly handleFocus = (): void => {
    this.beginHold();
  };

  private readonly handleBlur = (): void => {
    if (this.virtualPointerInsideOk) {
      return;
    }
    this.releaseHold();
  };

  private handlePointerSnapshot(snapshot: HoverPointerSnapshot): void {
    if (this.completed || this.root.hidden) {
      return;
    }
    const inside = this.isVirtualPointerOnOk(snapshot);
    if (inside === this.virtualPointerInsideOk) {
      return;
    }
    this.virtualPointerInsideOk = inside;
    if (inside) {
      this.beginHold();
    } else {
      this.releaseHold();
    }
  }

  private isVirtualPointerOnOk(snapshot: HoverPointerSnapshot): boolean {
    if (snapshot.kind !== "virtualTouch" || snapshot.position === null) {
      return false;
    }
    return resolveHoverActive(snapshot, this.okButton.getBoundingClientRect());
  }

  private beginHold(): void {
    if (this.completed) {
      return;
    }
    this.okButton.dataset.engaged = "true";
    this.chargeController.setHovered(true);
  }

  private releaseHold(): void {
    this.chargeController.setHovered(false);
    if (this.chargeController.getProgress() === 0) {
      this.okButton.dataset.engaged = "false";
      this.fill.style.transition = "height 200ms ease-out";
      this.fill.style.height = "0%";
    }
  }

  private cancelHold(): void {
    this.chargeController.cancel();
    this.okButton.dataset.engaged = "false";
    this.fill.style.transition = "height 0ms linear";
    this.fill.style.height = "0%";
  }

  private applyHoldProgress(progress: number): void {
    if (this.completed) {
      return;
    }
    this.okButton.dataset.engaged = progress > 0 ? "true" : "false";
    this.fill.style.transition = "height 80ms linear";
    this.fill.style.height = `${progress * 100}%`;
  }

  private complete(): void {
    if (this.completed) {
      return;
    }
    this.completed = true;
    this.fill.style.transition = "height 200ms ease-out";
    this.fill.style.height = "0%";
    this.okButton.dataset.completed = "true";
    this.virtualPointerInsideOk = false;
    this.okButton.dataset.engaged = "false";
    setTimeout(() => {
      this.root.hidden = true;
      this.setBackgroundInert(false);
      this.pointerModel.republish();
      this.onComplete();
    }, 200);
  }

  private setBackgroundInert(inert: boolean): void {
    const parent = this.root.parentElement;
    if (parent === null) return;
    for (const child of Array.from(parent.children)) {
      if (child === this.root) continue;
      if (inert) {
        child.setAttribute("inert", "");
      } else {
        child.removeAttribute("inert");
      }
    }
  }
}
